<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import RoundRobinUniversalAPI from 'dashboard/api/roundRobinUniversal';

// V1 · Round Robin Universal · 2026-05-13 (Victor)
// Toggle global + selector multi-agente. Cuando ON, todos los chats nuevos
// se asignan rotando entre los agentes seleccionados, sin importar la
// sucursal del inbox ni inbox_members.

const store = useStore();
const router = useRouter();
const { isAdminOrSuperAdmin } = usePermissions();

const enabled = ref(false);
const selectedAgentIds = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const initialEnabled = ref(false);
const initialAgentIds = ref([]);

const allAgents = computed(() => store.getters['agents/getAgents'] || []);
const branches = computed(() => store.getters['branches/getBranches'] || []);
const branchById = computed(() => {
  const map = {};
  (branches.value || []).forEach(b => {
    map[b.id] = b;
  });
  return map;
});

function agentBranchLabel(agent) {
  const pbId = agent.primary_branch_id;
  if (!pbId) return 'Sin sucursal';
  const b = branchById.value[pbId];
  return b?.code || b?.name || 'Sin sucursal';
}

function agentRoleLabel(agent) {
  if (agent?.role === 'administrator') return 'Admin';
  return 'Agent';
}

function agentInitials(agent) {
  const name = agent?.name || '';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('') || '?';
}

const isAgentSelected = id => selectedAgentIds.value.includes(id);

function toggleAgent(agent) {
  if (!enabled.value) return;
  const id = agent.id;
  if (isAgentSelected(id)) {
    selectedAgentIds.value = selectedAgentIds.value.filter(x => x !== id);
  } else {
    selectedAgentIds.value = [...selectedAgentIds.value, id];
  }
}

const selectedAgents = computed(() => {
  return selectedAgentIds.value
    .map(id => allAgents.value.find(a => a.id === id))
    .filter(Boolean);
});

const isDirty = computed(() => {
  if (enabled.value !== initialEnabled.value) return true;
  const a = [...selectedAgentIds.value].sort().join(',');
  const b = [...initialAgentIds.value].sort().join(',');
  return a !== b;
});

const canSave = computed(() => {
  if (isSaving.value) return false;
  if (!isDirty.value) return false;
  if (enabled.value && selectedAgentIds.value.length === 0) return false;
  return true;
});

async function loadState() {
  isLoading.value = true;
  try {
    await store.dispatch('agents/get');
    await store.dispatch('branches/get');
    const data = await RoundRobinUniversalAPI.get();
    enabled.value = Boolean(data.enabled);
    selectedAgentIds.value = Array.isArray(data.agent_ids) ? [...data.agent_ids] : [];
    initialEnabled.value = enabled.value;
    initialAgentIds.value = [...selectedAgentIds.value];
  } catch (e) {
    useAlert('No se pudo cargar la configuración');
  } finally {
    isLoading.value = false;
  }
}

async function save() {
  if (!canSave.value) return;
  isSaving.value = true;
  try {
    const payload = {
      enabled: enabled.value,
      agentIds: selectedAgentIds.value,
    };
    const data = await RoundRobinUniversalAPI.update(payload);
    enabled.value = Boolean(data.enabled);
    selectedAgentIds.value = Array.isArray(data.agent_ids) ? [...data.agent_ids] : [];
    initialEnabled.value = enabled.value;
    initialAgentIds.value = [...selectedAgentIds.value];
    useAlert('✅ Round Robin Universal actualizado');
  } catch (e) {
    useAlert('Error al guardar · revisa tu permiso de super admin');
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  if (!isAdminOrSuperAdmin.value) {
    router.replace({ name: 'settings_home' });
    return;
  }
  loadState();
});
</script>

<template>
  <div class="rru-wrap">
    <div class="rru-header">
      <div>
        <h1 class="rru-title">Round Robin Universal</h1>
        <p class="rru-subtitle">
          Asignación cross-inbox · todos los chats entrantes rotan entre los agentes
          seleccionados, sin importar la sucursal o el inbox de origen.
        </p>
      </div>
      <div class="rru-status-pill" :class="{ active: enabled }">
        {{ enabled ? '● Activo' : '○ Inactivo' }}
      </div>
    </div>

    <div v-if="isLoading" class="rru-loading">Cargando…</div>

    <template v-else>
      <!-- Toggle -->
      <section class="rru-card">
        <div class="rru-toggle-row">
          <button
            type="button"
            class="rru-toggle"
            :class="{ on: enabled }"
            @click="enabled = !enabled"
          >
            <span class="rru-toggle-knob"></span>
          </button>
          <div class="rru-toggle-text">
            <div class="rru-toggle-title">
              Modo universal: {{ enabled ? 'ACTIVADO' : 'DESACTIVADO' }}
            </div>
            <div class="rru-toggle-desc">
              <template v-if="enabled">
                Los chats nuevos se asignarán rotando entre los agentes seleccionados.
                Ignora filtros por sucursal y por inbox_members.
              </template>
              <template v-else>
                Cuando esté apagado, vuelve al comportamiento por sucursal (filtro de
                <code>branch.assignable_agents</code>).
              </template>
            </div>
          </div>
        </div>
      </section>

      <!-- Agentes -->
      <section class="rru-card" :class="{ disabled: !enabled }">
        <div class="rru-card-head">
          <div class="rru-card-title">Agentes en el pool universal</div>
          <div class="rru-card-hint">
            Selecciona los agentes que recibirán los chats. Mínimo 1 si activas el modo.
          </div>
        </div>

        <div v-if="allAgents.length === 0" class="rru-empty">
          No hay agentes en la cuenta.
        </div>
        <div v-else class="rru-agent-grid">
          <button
            v-for="agent in allAgents"
            :key="agent.id"
            type="button"
            class="rru-agent-card"
            :class="{ selected: isAgentSelected(agent.id), disabled: !enabled }"
            @click="toggleAgent(agent)"
          >
            <div class="rru-agent-avatar">
              <img v-if="agent.avatar_url" :src="agent.avatar_url" alt="" />
              <span v-else>{{ agentInitials(agent) }}</span>
            </div>
            <div class="rru-agent-info">
              <div class="rru-agent-name">{{ agent.name }}</div>
              <div class="rru-agent-meta">
                {{ agentBranchLabel(agent) }} · {{ agentRoleLabel(agent) }}
              </div>
            </div>
            <div class="rru-agent-check">
              <span v-if="isAgentSelected(agent.id)">✓</span>
            </div>
          </button>
        </div>
      </section>

      <!-- Rotación -->
      <section v-if="enabled && selectedAgents.length > 0" class="rru-card">
        <div class="rru-card-head">
          <div class="rru-card-title">🔄 Orden de rotación (round-robin LIFO)</div>
          <div class="rru-card-hint">
            La cola Redis se rebalancea automáticamente cada vez que cambian los agentes
            del pool. El siguiente chat entrante va al primer agente disponible.
          </div>
        </div>
        <div class="rru-rotation-chain">
          <template v-for="(agent, idx) in selectedAgents" :key="`rot-${agent.id}`">
            <div class="rru-rotation-item">
              <div class="rru-rotation-avatar">{{ agentInitials(agent) }}</div>
              <span>{{ agent.name }}</span>
            </div>
            <span v-if="idx < selectedAgents.length - 1" class="rru-rotation-arrow">→</span>
            <span v-else class="rru-rotation-arrow">↻</span>
          </template>
        </div>
      </section>

      <!-- Save bar -->
      <div class="rru-actions">
        <button
          type="button"
          class="rru-btn rru-btn-save"
          :disabled="!canSave"
          @click="save"
        >
          {{ isSaving ? 'Guardando…' : '💾 Guardar' }}
        </button>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.rru-wrap {
  padding: 24px 32px 80px;
  max-width: 980px;
  margin: 0 auto;
  font-family: -apple-system, 'SF Pro Display', 'Inter', sans-serif;
}

.rru-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 22px;
}
.rru-title {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #f4d57a 0%, #c89b3f 50%, #8b6914 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 6px;
}
.rru-subtitle {
  font-size: 13px;
  color: #8a7960;
  max-width: 640px;
  line-height: 1.5;
  margin: 0;
}
.rru-status-pill {
  background: rgba(60, 55, 45, 0.45);
  border: 1px solid rgba(196, 150, 60, 0.25);
  color: #8a7960;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  white-space: nowrap;
  flex-shrink: 0;
}
.rru-status-pill.active {
  background: linear-gradient(135deg, rgba(40, 180, 90, 0.18), rgba(20, 80, 40, 0.4));
  border-color: rgba(40, 180, 90, 0.55);
  color: #6cd497;
}

.rru-loading {
  padding: 36px;
  text-align: center;
  color: #8a7960;
}

.rru-card {
  background: linear-gradient(160deg, rgba(40, 30, 18, 0.55), rgba(15, 12, 8, 0.85));
  border: 1px solid rgba(196, 150, 60, 0.18);
  border-radius: 16px;
  padding: 22px 24px;
  margin-bottom: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  transition: opacity 0.18s;
}
.rru-card.disabled {
  opacity: 0.55;
}

.rru-card-head {
  margin-bottom: 16px;
}
.rru-card-title {
  color: #f4d57a;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}
.rru-card-hint {
  color: #8a7960;
  font-size: 12px;
  line-height: 1.55;
}

.rru-toggle-row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.rru-toggle {
  position: relative;
  width: 46px;
  height: 26px;
  background: rgba(60, 55, 45, 0.7);
  border-radius: 13px;
  border: 1px solid rgba(196, 150, 60, 0.25);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition: background 0.2s;
}
.rru-toggle.on {
  background: linear-gradient(135deg, #f4d57a, #c89b3f);
  border-color: #c89b3f;
}
.rru-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #8a7960;
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.rru-toggle.on .rru-toggle-knob {
  left: 22px;
  background: #1a1410;
}
.rru-toggle-text {
  flex: 1;
}
.rru-toggle-title {
  color: #f4d57a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 3px;
}
.rru-toggle-desc {
  color: #a89472;
  font-size: 12px;
  line-height: 1.55;
}
.rru-toggle-desc code {
  background: rgba(196, 150, 60, 0.15);
  padding: 1px 5px;
  border-radius: 4px;
  color: #f4d57a;
  font-size: 11px;
}

.rru-empty {
  color: #8a7960;
  font-size: 13px;
  padding: 18px;
  text-align: center;
}

.rru-agent-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (max-width: 720px) {
  .rru-agent-grid { grid-template-columns: 1fr; }
}

.rru-agent-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(20, 15, 10, 0.5);
  border: 1px solid rgba(196, 150, 60, 0.15);
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.18s;
  text-align: left;
  font-family: inherit;
  color: inherit;
}
.rru-agent-card:hover {
  border-color: rgba(196, 150, 60, 0.4);
}
.rru-agent-card.selected {
  background: linear-gradient(135deg, rgba(196, 150, 60, 0.18), rgba(40, 30, 18, 0.5));
  border-color: rgba(196, 150, 60, 0.55);
  box-shadow: 0 2px 12px rgba(196, 150, 60, 0.15);
}
.rru-agent-card.disabled {
  cursor: not-allowed;
  pointer-events: none;
}
.rru-agent-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c89b3f, #8b6914);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
}
.rru-agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rru-agent-info { flex: 1; min-width: 0; }
.rru-agent-name {
  color: #e8dcc4;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rru-agent-meta {
  color: #8a7960;
  font-size: 11px;
  margin-top: 2px;
}
.rru-agent-check {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1.5px solid rgba(196, 150, 60, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #1a1410;
  font-weight: 800;
  font-size: 13px;
}
.rru-agent-card.selected .rru-agent-check {
  background: linear-gradient(135deg, #f4d57a, #c89b3f);
  border-color: #f4d57a;
}

.rru-rotation-chain {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.rru-rotation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(196, 150, 60, 0.18), rgba(40, 30, 18, 0.5));
  border: 1px solid rgba(196, 150, 60, 0.4);
  padding: 4px 11px 4px 5px;
  border-radius: 16px;
  color: #f4d57a;
  font-size: 12px;
  font-weight: 600;
}
.rru-rotation-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c89b3f, #8b6914);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 10px;
}
.rru-rotation-arrow {
  color: rgba(196, 150, 60, 0.6);
  font-size: 14px;
  font-weight: 700;
}

.rru-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 26px;
}
.rru-btn-save {
  background: linear-gradient(135deg, #f4d57a, #c89b3f);
  color: #1a1410;
  border: none;
  padding: 10px 26px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(196, 150, 60, 0.3);
  transition: opacity 0.18s, transform 0.18s;
}
.rru-btn-save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.rru-btn-save:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(196, 150, 60, 0.4);
}
</style>
